import { Trans, useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { addToast, Checkbox } from '@heroui/react'
import posthog from 'posthog-js'
import {
    ApiExchangeSelection,
    PkgInfrastructureRestRefundMethod
} from '@itsrever/returns-api-types'
import { LoadingModal } from '@/components/Loading'
import { useCalculateSummary } from '@/hooks/use-calculate-summary'
import {
    cleanProcess,
    returnsApi,
    updateCalculate
} from '@/redux/api/returnsApiSlice'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { CreditCardSlash } from '@/ui/icons/credit-card-slash'
import { DeliveryTruckIcon } from '@/ui/icons/delivery-truck'
import { ShoppingBagIcon } from '@/ui/icons/shopping-bag'
import { formatPrice } from '@/utils'
import { usePostHog } from '@/application/use-posthog'
import { LineItemSubtypes } from '@/redux/return/returnSlice'

interface GiftCardOfferProps {
    giftCard: PkgInfrastructureRestRefundMethod
}

export const GiftCardOffer = ({ giftCard: { method } }: GiftCardOfferProps) => {
    const { t, i18n } = useTranslation()

    const dispatch = useAppDispatch()

    const { captureEventPosthog } = usePostHog()

    const settings = useAppSelector(
        (store) => store.settingsApi.settings.response
    )

    const order = useAppSelector((store) => store.returnsApi.order.response)

    const returnProcess = useAppSelector((store) => store.return)

    const getSummaryData = (checked: boolean) => {
        return {
            ecommerceID: settings.ecommerce_id ?? '',
            calculateData: {
                checkout_id: order.checkout_id ?? '',
                drop_off_address: returnProcess.returnAddress,
                exchange_shipping_address: returnProcess.exchangeAddress,
                exchange_selections: returnProcess.newItems.map((item) => {
                    const exchangeItem: ApiExchangeSelection = {
                        line_item_platform_id: item.returnedItemId,
                        quantity: item.quantity,
                        variant_id: item.productVariant.variantId,
                        signed_product_raw: item.signedProductRaw,
                        collection_ids: item.collectionIds
                    }

                    return exchangeItem
                }),
                pickup_address: returnProcess.returnAddress,
                pickup_ranges: returnProcess.pickUpRanges,
                return_method: returnProcess.returnMethod,
                items_to_return: returnProcess.returnedItems.map((p) => {
                    return p.item
                }),
                user_preferred_lang: i18n.language,
                user_selected_carrier: returnProcess.userSelectedCarrier,
                user_selected_provider: returnProcess.userSelectedProvider,
                refund_method: checked
                    ? method
                    : returnProcess.refundPaymentMethod.method,
                keep_your_item_selected: returnProcess.keepYourItem
            }
        }
    }

    const { data } = useQuery({
        queryKey: ['gift-card-calculated-summary'],
        queryFn: async () => {
            const { data } = await returnsApi.calculate(getSummaryData(true))

            return data
        },
        staleTime: 0
    })

    const { mutate, isPending } = useCalculateSummary()

    const selectGiftCard = (checked: boolean) => {
        if (!order.checkout_id || !settings.ecommerce_id) {
            return
        }

        captureEventPosthog('Gift Card Offer Clicked', {
            checked,
            ecommerceId: settings.ecommerce_id
        })

        mutate(getSummaryData(checked), {
            onSuccess: (data) => {
                dispatch(updateCalculate(data))
                dispatch(cleanProcess())
            },
            onError: () => {
                addToast({
                    description: t('error_406'),
                    color: 'danger'
                })
            }
        })
    }

    const shippingCost = data?.calculated_summary?.costs?.find(
        (cost) => cost.subtype === LineItemSubtypes.ShippingCost
    )

    const hasFreeShipping = !shippingCost || shippingCost.total === 0

    const shouldShowGiftCardOffer =
        hasFreeShipping &&
        posthog.getFeatureFlag('giftcard-option-in-the-summary-page') === 'test'

    return shouldShowGiftCardOffer ? (
        <>
            {isPending ? <LoadingModal loading /> : null}
            <label className="my-4 block cursor-pointer rounded-lg border-2 border-solid border-blue-400 bg-gradient-to-br from-blue-100/70 via-blue-50/40 p-4 shadow-[0px_0px_4px_0px_rgba(59,_130,_246,_0.5)]">
                <span className="flex items-center text-base font-semibold">
                    <Checkbox
                        onValueChange={selectGiftCard}
                        color="primary"
                        radius="sm"
                        classNames={{
                            label: 'font-semibold font-roobert text-base',
                            wrapper:
                                'bg-white group-data-[selected=true]:before:bg-neutral-800'
                        }}
                    />
                    <Trans
                        i18nKey="confirmation.gift_card_label"
                        components={{
                            span: (
                                <span className="ml-2 inline-flex items-center rounded-md border border-solid border-neutral-800 bg-neutral-800 px-2 py-1 text-xs tracking-wide text-white" />
                            )
                        }}
                    />
                </span>
                <div className="space-y-2 pl-7 pt-1 text-sm font-medium text-neutral-600">
                    <p className="max-w-[19rem] text-sm font-medium">
                        <Trans
                            i18nKey="confirmation.gift_card_offer"
                            components={{
                                span: <strong />
                            }}
                        />
                    </p>
                    <ul className="space-y-1.5 font-[Roobert]">
                        <li className="flex items-center gap-2">
                            <DeliveryTruckIcon width={16} height={16} />
                            {t('confirmation.gift_card_free_shipping')}
                        </li>
                        {data?.signed_summary?.return_summary
                            ?.total_refund_amount ? (
                            <li className="flex items-center gap-2">
                                <ShoppingBagIcon width={16} height={16} />
                                <p className="text-sm">
                                    <Trans
                                        i18nKey="confirmation.gift_card_total_refund"
                                        values={{
                                            totalRefundAmount: formatPrice(
                                                data.signed_summary
                                                    .return_summary
                                                    .total_refund_amount,
                                                order.order?.money_format ?? {}
                                            )
                                        }}
                                        components={{
                                            span: (
                                                <span className="text-base font-semibold text-[#1790FF]" />
                                            )
                                        }}
                                    />
                                </p>
                            </li>
                        ) : null}
                        <li className="flex items-center gap-2">
                            <CreditCardSlash width={16} height={16} />
                            {t('confirmation.gift_card_return_fee')}
                        </li>
                    </ul>
                </div>
            </label>
        </>
    ) : null
}
